import React, { useEffect, useMemo, useState } from 'react';import {
  Calendar,
  CalendarDays,
  CalendarPlus,
  CalendarClock,
  Activity,
  Bell,
  CheckCircle,
  CheckCircle2,
  Clock,
  User,
  Video,
  VideoOff,
  Search,
  History,
  Star,
  MapPin,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  XCircle,
  Check,
  Eye,
  MessageSquare,
  TrendingUp,
  Send,
  X,
  RotateCcw,
  Paperclip,
  Pill,
  ClipboardList,
  ThumbsUp,
  ThumbsDown,
  Bug,
  Lightbulb,
  ArrowUpDown,
  Timer,
  Loader2,
} from 'lucide-react';
import DashboardLayout from '../../components/common/DashboardLayout';
import { Card, LoadingState, StatusBadge, Badge, EmptyState, Button, JoinGMeetButton, LicensedDoctorBadge, DoctorLicenseModal } from '../../components/common/UI';
import { useAuth } from '../../context/AuthContext';
import { appointmentsAPI, prescriptionsAPI, notificationsAPI, usersAPI } from '../../services/api';
import toast from 'react-hot-toast';

// ── Shared nav for all patient pages ────────────────────────────────────────
const NAV_ITEMS = [
  { path: '/patient/dashboard',     label: 'Dashboard',        icon: Activity },
  { path: '/patient/appointments',  label: 'Appointments',     icon: Calendar },
  { path: '/patient/prescriptions', label: 'Prescriptions',    icon: Pill     },
  { path: '/patient/progress',      label: 'Therapy Progress', icon: Activity },
  { path: '/patient/notifications', label: 'Notifications',    icon: Bell     },
];

/* =============================================================================
 * HOW TO USE THIS FILE
 * -----------------------------------------------------------------------------
 * Your PatientSubPages.js already contains PatientAppointments, PatientPrescriptions,
 * PatientProgress, and PatientNotifications in one file. This file replaces ONLY
 * the PatientAppointments section — everything from the old
 *   "// ============================================================
 *    // 1. APPOINTMENTS
 *    // ============================================================"
 * comment down to (but not including) the
 *   "// ============================================================
 *    // 2. PRESCRIPTIONS"
 * comment. Leave PatientPrescriptions / PatientProgress / PatientNotifications,
 * your imports, NAV_ITEMS, and the shared `fmt` helper exactly as they are.
 *
 * STEP 1 — Update your top-of-file `lucide-react` import in PatientSubPages.js
 * to include the extra icons this section needs (merge with what's already
 * imported — don't duplicate names like Calendar, Bell, Pill, Star, Clock,
 * User, Video, Search, History, MapPin, ChevronRight, AlertCircle, XCircle,
 * Check, Eye, MessageSquare, TrendingUp, Send that you likely already have):
 *
 *   CalendarDays, CalendarPlus, CalendarClock, CheckCircle, CheckCircle2,
 *   VideoOff, ChevronLeft, X, RotateCcw, Paperclip, ClipboardList,
 *   ThumbsUp, ThumbsDown, Bug, Lightbulb, ArrowUpDown, Timer, Loader2
 *
 * STEP 2 — Replace the PatientAppointments section (see above) with everything
 * below this comment block, keeping NAV_ITEMS and `fmt` where they already are
 * in your file (this section adds a few more small date helpers alongside them:
 * fmtShort, fmtWeekday, combineDateTime, addMinutes, isSameDay, useNow,
 * formatCountdown — these are new, so just paste them in).
 *
 * STEP 3 — Backend endpoints this section calls on `appointmentsAPI`. If your
 * services/api.js doesn't have these yet, the related buttons show a toast
 * saying the endpoint isn't configured instead of crashing — add them when
 * your backend is ready:
 *
 *   appointmentsAPI.reschedule(id, { date, startTime })
 *       PUT /api/appointments/:id/reschedule
 *       Body: { date: ISOString, startTime: "HH:mm" } → { appointment }
 *
 *   appointmentsAPI.cancel(id, { reason })
 *       PUT /api/appointments/:id/cancel
 *       Body: { reason?: string } → { appointment }
 *
 *   appointmentsAPI.getMeetingLink(id)
 *       GET /api/appointments/:id/meeting
 *       → { joinUrl, startsAt, endsAt, provider }
 *       (backend owns Google Meet creation — no client-side API keys)
 *
 *   appointmentsAPI.submitReview(id, payload)
 *       POST /api/appointments/:id/review
 *       Body: { overall, communication, professionalism, treatment,
 *               waitingTime, review, anonymous } → { review }
 *
 *   A new feedbackAPI.submitAppFeedback(payload) — POST /api/feedback/app —
 *   is needed for the standalone app-feedback tab (not tied to one appointment).
 * ============================================================================= */

// ============================================================
// 1. APPOINTMENTS
// ============================================================
const fmt = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtShort = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—';

const fmtWeekday = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { weekday: 'short' }) : '');

// Combines an appointment's `date` (day) with its `startTime` ("HH:mm") into a real Date.
const combineDateTime = (date, time) => {
  if (!date || !time) return null;
  const base = new Date(date);
  const [h, m] = String(time).split(':').map(Number);
  base.setHours(h || 0, m || 0, 0, 0);
  return base;
};

const addMinutes = (d, mins) => new Date(d.getTime() + mins * 60000);

const isSameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

function useNow(intervalMs = 30000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

function formatCountdown(ms) {
  if (ms <= 0) return '00:00:00';
  const totalSec = Math.floor(ms / 1000);
  const h = String(Math.floor(totalSec / 3600)).padStart(2, '0');
  const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
  const s = String(totalSec % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

// ============================================================
// Reusable pieces
// ============================================================

const StatCard = ({ icon: Icon, label, value, tone = 'emerald', trend }) => {
  const tones = {
    emerald: 'from-emerald-500 to-teal-500 bg-emerald-50 text-emerald-600',
    blue: 'from-blue-500 to-cyan-500 bg-blue-50 text-blue-600',
    amber: 'from-amber-500 to-orange-500 bg-amber-50 text-amber-600',
    red: 'from-rose-500 to-red-500 bg-rose-50 text-rose-600',
    violet: 'from-violet-500 to-purple-500 bg-violet-50 text-violet-600',
  };
  const [gradient, , iconTone] = tones[tone].split(' bg-');
  return (
    <div className="relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg group">
      <div
        className={`absolute -right-6 -top-6 w-24 h-24 rounded-full bg-gradient-to-br ${gradient} opacity-10 group-hover:opacity-20 transition-opacity`}
      />
      <div className="flex items-center justify-between">
        <div className={`w-11 h-11 rounded-xl bg-${tones[tone].split('bg-')[1].split(' ')[0]} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${iconTone}`} />
        </div>
        {trend !== undefined && (
          <span
            className={`text-xs font-semibold flex items-center gap-0.5 ${
              trend >= 0 ? 'text-emerald-600' : 'text-rose-500'
            }`}
          >
            <TrendingUp className="w-3 h-3" />
            {trend >= 0 ? '+' : ''}
            {trend}%
          </span>
        )}
      </div>
      <p className="mt-4 text-2xl font-bold text-gray-800">{value}</p>
      <p className="text-sm text-gray-500 mt-0.5">{label}</p>
    </div>
  );
};

const StarRating = ({ value, onChange, size = 22, readOnly = false }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        disabled={readOnly}
        onClick={() => onChange && onChange(star)}
        className={`transition-all ${readOnly ? 'cursor-default' : 'hover:scale-110'}`}
      >
        <Star size={size} className={star <= value ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} />
      </button>
    ))}
  </div>
);

const SkeletonCard = () => (
  <div className="rounded-2xl border border-gray-100 p-4 animate-pulse">
    <div className="flex gap-4">
      <div className="w-14 h-14 rounded-xl bg-gray-100 flex-shrink-0" />
      <div className="flex-1 space-y-2 py-1">
        <div className="h-3.5 bg-gray-100 rounded w-1/3" />
        <div className="h-3 bg-gray-100 rounded w-1/2" />
        <div className="h-3 bg-gray-100 rounded w-1/4" />
      </div>
    </div>
  </div>
);

// Consultation progress timeline
const TIMELINE_STAGES = [
  { key: 'booked', label: 'Appointment Booked' },
  { key: 'confirmed', label: 'Doctor Confirmed' },
  { key: 'reminderSent', label: 'Reminder Sent' },
  { key: 'checkedIn', label: 'Patient Checked In' },
  { key: 'started', label: 'Consultation Started' },
  { key: 'completed', label: 'Consultation Completed' },
  { key: 'prescriptionGenerated', label: 'Prescription Generated' },
  { key: 'feedbackSubmitted', label: 'Feedback Submitted' },
];

// Derives which stage index an appointment is currently at from its status /
// optional `timeline` map returned by the backend ({ [stageKey]: ISODate }).
function currentStageIndex(appt) {
  if (!appt) return 0;
  if (appt.timeline) {
    let idx = -1;
    TIMELINE_STAGES.forEach((s, i) => {
      if (appt.timeline[s.key]) idx = i;
    });
    if (idx >= 0) return idx;
  }
  switch (appt.status) {
    case 'cancelled':
    case 'missed':
      return 1;
    case 'completed':
      return appt.prescriptionAvailable ? 6 : 5;
    case 'in-progress':
    case 'ongoing':
      return 4;
    case 'confirmed':
      return 1;
    default:
      return 0;
  }
}

const AppointmentTimeline = ({ appointment }) => {
  const activeIdx = currentStageIndex(appointment);
  const cancelled = appointment?.status === 'cancelled' || appointment?.status === 'missed';
  return (
    <div className="py-2">
      {TIMELINE_STAGES.map((stage, i) => {
        const done = i < activeIdx || (i === activeIdx && appointment?.status === 'completed');
        const current = i === activeIdx && !done;
        return (
          <div key={stage.key} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold border-2 transition-colors
                  ${
                    cancelled && i >= activeIdx
                      ? 'bg-gray-100 border-gray-200 text-gray-400'
                      : done
                      ? 'bg-emerald-500 border-emerald-500 text-white'
                      : current
                      ? 'bg-white border-emerald-500 text-emerald-600 animate-pulse'
                      : 'bg-gray-50 border-gray-200 text-gray-300'
                  }`}
              >
                {done ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              {i < TIMELINE_STAGES.length - 1 && (
                <div className={`w-0.5 flex-1 min-h-[24px] ${done ? 'bg-emerald-400' : 'bg-gray-200'}`} />
              )}
            </div>
            <div className={`pb-6 ${current ? 'text-emerald-700' : done ? 'text-gray-700' : 'text-gray-400'}`}>
              <p className={`text-sm font-semibold ${current ? 'animate-pulse' : ''}`}>{stage.label}</p>
              {appointment?.timeline?.[stage.key] && (
                <p className="text-xs text-gray-400 mt-0.5">
                  {new Date(appointment.timeline[stage.key]).toLocaleString('en-IN')}
                </p>
              )}
            </div>
          </div>
        );
      })}
      {cancelled && (
        <div className="flex items-center gap-2 text-rose-600 text-sm font-medium bg-rose-50 border border-rose-100 rounded-lg px-3 py-2 -mt-2">
          <XCircle className="w-4 h-4" />
          This appointment was {appointment.status}.
        </div>
      )}
    </div>
  );
};

// Join-meeting button: enabled only within the live window, counts down
// before start, and disables itself once the session has ended.
const JoinMeetingButton = ({ appointment, onNeedsLink }) => {
  const now = useNow(1000);
  if (appointment.type && appointment.type.toLowerCase() !== 'online') return null;

  const start = combineDateTime(appointment.date, appointment.startTime);
  const duration = appointment.duration || 30;
  const end = start ? addMinutes(start, duration) : null;
  if (!start || !end) return null;

  const joinWindowOpensAt = addMinutes(start, -10); // allow joining 10 min early
  const isBeforeWindow = now < joinWindowOpensAt;
  const isLive = now >= joinWindowOpensAt && now <= end;
  const isOver = now > end;

  if (isOver || appointment.status === 'cancelled' || appointment.status === 'completed') {
    return (
      <Button size="sm" variant="outline" disabled className="opacity-50 cursor-not-allowed">
        <VideoOff className="w-4 h-4 mr-2" />
        Session Ended
      </Button>
    );
  }

  if (isBeforeWindow) {
    return (
      <div className="flex items-center gap-2 text-xs font-medium text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
        <Timer className="w-3.5 h-3.5" />
        Starts in {formatCountdown(joinWindowOpensAt - now)}
      </div>
    );
  }

  return (
    <Button
      size="sm"
      onClick={() => {
        const url = appointment.meetingLink;
        if (!url) {
          onNeedsLink && onNeedsLink(appointment);
          return;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
      }}
      className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 animate-pulse"
    >
      <Video className="w-4 h-4 mr-2" />
      Join Consultation
    </Button>
  );
};

// ============================================================
// Upcoming appointment card
// ============================================================
const UpcomingAppointmentCard = ({ appt, onView, onReschedule, onCancel, onNeedsLink, onInspectDoctor }) => {
  const start = combineDateTime(appt.date, appt.startTime);
  const isToday = start && isSameDay(start, new Date());

  const addToCalendar = () => {
    if (!start) return;
    const end = addMinutes(start, appt.duration || 30);
    const toICSDate = (d) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'BEGIN:VEVENT',
      `DTSTART:${toICSDate(start)}`,
      `DTEND:${toICSDate(end)}`,
      `SUMMARY:${appt.therapy?.name || 'Therapy Session'} with Dr. ${appt.doctor?.name || ''}`,
      `LOCATION:${appt.clinicName || appt.type || ''}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\n');
    const blob = new Blob([ics], { type: 'text/calendar' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `appointment-${appt._id}.ics`;
    link.click();
  };

  return (
    <div className="relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 shadow-sm hover:shadow-lg hover:border-emerald-200 transition-all">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400" />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {appt.doctor?.photo ? (
              <img src={appt.doctor.photo} alt={appt.doctor?.name} className="w-full h-full object-cover" />
            ) : (
              <User className="w-6 h-6 text-emerald-600" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-bold text-gray-800">Dr. {appt.doctor?.name || 'Unassigned'}</p>
              {appt.doctor && <LicensedDoctorBadge doctor={appt.doctor} onClick={() => onInspectDoctor && onInspectDoctor(appt.doctor)} />}
            </div>
            <p className="text-sm text-emerald-600 font-medium">{appt.doctor?.specialization || '—'}</p>
            <p className="text-xs text-gray-400 mt-0.5">{appt.therapy?.name}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {isToday && <Badge color="amber">Today</Badge>}
          <StatusBadge status={appt.status} />
          <span className="text-xs text-gray-400">#{appt.sessionId || appt._id?.slice(-6)}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
        <div className="flex items-center gap-1.5 text-gray-600">
          <Calendar className="w-4 h-4 text-emerald-500" />
          {fmt(appt.date)}
        </div>
        <div className="flex items-center gap-1.5 text-gray-600">
          <Clock className="w-4 h-4 text-emerald-500" />
          {appt.startTime}
        </div>
        <div className="flex items-center gap-1.5 text-gray-600">
          <Timer className="w-4 h-4 text-emerald-500" />
          {appt.duration || 30} min
        </div>
        <div className="flex items-center gap-1.5 text-gray-600">
          {appt.type?.toLowerCase() === 'online' ? (
            <Video className="w-4 h-4 text-emerald-500" />
          ) : (
            <MapPin className="w-4 h-4 text-emerald-500" />
          )}
          {appt.type?.toLowerCase() === 'online' ? 'Online' : appt.clinicName || 'In-clinic'}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-5 pt-4 border-t border-gray-50">
        <Button size="sm" variant="outline" onClick={() => onView(appt)}>
          <Eye className="w-4 h-4 mr-2" />
          View Details
        </Button>
        {appt.status !== 'cancelled' && appt.status !== 'completed' && (
          <>
            <Button size="sm" variant="outline" onClick={() => onReschedule(appt)}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Reschedule
            </Button>
            <Button size="sm" variant="outline" onClick={() => onCancel(appt)} className="text-rose-600 border-rose-200 hover:bg-rose-50">
              <XCircle className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </>
        )}
        <Button size="sm" variant="outline" onClick={addToCalendar}>
          <CalendarPlus className="w-4 h-4 mr-2" />
          Add to Calendar
        </Button>
        <div className="ml-auto">
          <JoinGMeetButton appointment={appt} />
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Past appointment row + history controls
// ============================================================
const PastAppointmentRow = ({ appt, onView, onReview }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border border-gray-100 rounded-2xl hover:bg-gray-50/60 transition-all">
    <div className="w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
      <History className="w-5 h-5 text-gray-500" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-gray-800 truncate">{appt.therapy?.name || appt.type}</p>
      <p className="text-sm text-gray-500 truncate">
        Dr. {appt.doctor?.name} &bull; {fmt(appt.date)} &bull; {appt.duration || 30} min
      </p>
      {appt.doctorNotes && <p className="text-xs text-gray-400 italic mt-1 truncate">"{appt.doctorNotes}"</p>}
    </div>
    <div className="flex items-center gap-2 flex-wrap">
      {appt.prescriptionAvailable && <Badge color="blue">Prescription</Badge>}
      {appt.reportAvailable && <Badge color="violet">Report</Badge>}
      <StatusBadge status={appt.status} />
    </div>
    <div className="flex items-center gap-2">
      <Button size="sm" variant="outline" onClick={() => onView(appt)}>
        <Eye className="w-4 h-4 mr-2" />
        Details
      </Button>
      {appt.status === 'completed' && !appt.reviewed && (
        <Button size="sm" onClick={() => onReview(appt)}>
          <Star className="w-4 h-4 mr-2" />
          Review
        </Button>
      )}
    </div>
  </div>
);

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'upcoming', label: 'Upcoming First' },
  { value: 'completed', label: 'Completed First' },
];

const PastAppointmentHistory = ({ appointments, onView, onReview }) => {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(1);
  const pageSize = 5;

  const doctors = useMemo(
    () => [...new Set(appointments.map((a) => a.doctor?.name).filter(Boolean))],
    [appointments]
  );
  const [doctorFilter, setDoctorFilter] = useState('all');

  const filtered = useMemo(() => {
    let list = appointments.filter((a) => a.status === 'completed' || a.status === 'cancelled' || a.status === 'missed');
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (a) =>
          a.doctor?.name?.toLowerCase().includes(q) ||
          a.therapy?.name?.toLowerCase().includes(q) ||
          a.status?.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') list = list.filter((a) => a.status === statusFilter);
    if (typeFilter !== 'all') list = list.filter((a) => a.type?.toLowerCase() === typeFilter);
    if (doctorFilter !== 'all') list = list.filter((a) => a.doctor?.name === doctorFilter);

    list = [...list].sort((a, b) => {
      const da = new Date(a.date).getTime();
      const db = new Date(b.date).getTime();
      if (sortBy === 'newest') return db - da;
      if (sortBy === 'oldest') return da - db;
      if (sortBy === 'upcoming') return da - db;
      if (sortBy === 'completed') return (a.status === 'completed' ? -1 : 1) - (b.status === 'completed' ? -1 : 1);
      return 0;
    });
    return list;
  }, [appointments, query, statusFilter, typeFilter, doctorFilter, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => setPage(1), [query, statusFilter, typeFilter, doctorFilter, sortBy]);

  return (
    <Card title="Past Appointment History" subtitle={`${filtered.length} record${filtered.length === 1 ? '' : 's'}`}>
      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by doctor, therapy, or status..."
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
          />
        </div>
        <select
          value={doctorFilter}
          onChange={(e) => setDoctorFilter(e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
        >
          <option value="all">All Doctors</option>
          {doctors.map((d) => (
            <option key={d} value={d}>
              Dr. {d}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
        >
          <option value="all">All Status</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
          <option value="missed">Missed</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
        >
          <option value="all">All Types</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
        </select>
        <div className="relative">
          <ArrowUpDown className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-emerald-500 outline-none appearance-none"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {pageItems.length === 0 ? (
        <EmptyState icon={History} title="No past appointments" description="Completed or past sessions matching your filters will appear here" />
      ) : (
        <div className="space-y-3">
          {pageItems.map((a) => (
            <PastAppointmentRow key={a._id} appt={a} onView={onView} onReview={onReview} />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-50">
          <p className="text-xs text-gray-400">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="outline" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

// ============================================================
// Smart reminders
// ============================================================
const SmartReminders = ({ appointments, notifications }) => {
  const now = useNow(60000);

  const cards = useMemo(() => {
    const items = [];
    appointments
      .filter((a) => a.status !== 'cancelled' && a.status !== 'completed' && a.status !== 'missed')
      .forEach((a) => {
        const start = combineDateTime(a.date, a.startTime);
        if (!start) return;
        const diffMs = start - now;
        const diffMin = diffMs / 60000;
        if (diffMin > 0 && diffMin <= 30) {
          items.push({
            id: `${a._id}-30min`,
            icon: AlertCircle,
            tone: 'red',
            title: 'Consultation Starts Soon',
            desc: `Dr. ${a.doctor?.name} in ${Math.round(diffMin)} min`,
            countdown: formatCountdown(diffMs),
          });
        } else if (isSameDay(start, now)) {
          items.push({
            id: `${a._id}-today`,
            icon: Bell,
            tone: 'amber',
            title: 'Appointment Today',
            desc: `Dr. ${a.doctor?.name} at ${a.startTime}`,
          });
        } else {
          const tomorrow = new Date(now);
          tomorrow.setDate(tomorrow.getDate() + 1);
          if (isSameDay(start, tomorrow)) {
            items.push({
              id: `${a._id}-tmrw`,
              icon: CalendarClock,
              tone: 'blue',
              title: 'Appointment Tomorrow',
              desc: `Dr. ${a.doctor?.name} at ${a.startTime}`,
            });
          }
        }
        if (a.followUpDate && isSameDay(new Date(a.followUpDate), now)) {
          items.push({
            id: `${a._id}-followup`,
            icon: ClipboardList,
            tone: 'violet',
            title: 'Follow-up Reminder',
            desc: `Follow-up due for ${a.therapy?.name || 'your therapy'}`,
          });
        }
      });

    (notifications || [])
      .filter((n) => n.type === 'reminder' && !n.isRead)
      .forEach((n) =>
        items.push({ id: n._id, icon: Pill, tone: 'emerald', title: n.title || 'Medicine Reminder', desc: n.message })
      );

    return items;
  }, [appointments, notifications, now]);

  if (cards.length === 0) return null;

  const toneClasses = {
    red: 'bg-rose-50 border-rose-100 text-rose-700',
    amber: 'bg-amber-50 border-amber-100 text-amber-700',
    blue: 'bg-blue-50 border-blue-100 text-blue-700',
    violet: 'bg-violet-50 border-violet-100 text-violet-700',
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-700',
  };

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Bell className="w-4 h-4 text-gray-500" />
        <h2 className="text-sm font-bold text-gray-600 uppercase tracking-wider">Smart Reminders</h2>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {cards.map((c) => (
          <div
            key={c.id}
            className={`flex items-center gap-3 rounded-xl border px-4 py-3 flex-shrink-0 min-w-[240px] ${toneClasses[c.tone]}`}
          >
            <c.icon className="w-5 h-5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold">{c.title}</p>
              <p className="text-xs opacity-80">{c.desc}</p>
              {c.countdown && <p className="text-xs font-mono font-bold mt-0.5">{c.countdown}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================
// Calendar view (month / week / day)
// ============================================================
const CalendarView = ({ appointments, onView }) => {
  const [mode, setMode] = useState('month'); // month | week | day
  const [cursor, setCursor] = useState(new Date());

  const apptsByDay = useMemo(() => {
    const map = new Map();
    appointments.forEach((a) => {
      if (!a.date) return;
      const key = new Date(a.date).toDateString();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    });
    return map;
  }, [appointments]);

  const statusDotColor = (status) =>
    ({
      completed: 'bg-emerald-500',
      upcoming: 'bg-blue-500',
      confirmed: 'bg-blue-500',
      pending: 'bg-blue-500',
      cancelled: 'bg-rose-400',
      missed: 'bg-gray-400',
    }[status] || 'bg-gray-400');

  const shift = (amount) => {
    const d = new Date(cursor);
    if (mode === 'month') d.setMonth(d.getMonth() + amount);
    else if (mode === 'week') d.setDate(d.getDate() + amount * 7);
    else d.setDate(d.getDate() + amount);
    setCursor(d);
  };

  const monthGrid = useMemo(() => {
    const start = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const startWeekday = start.getDay();
    const gridStart = new Date(start);
    gridStart.setDate(gridStart.getDate() - startWeekday);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(gridStart);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [cursor]);

  const weekDays = useMemo(() => {
    const start = new Date(cursor);
    start.setDate(start.getDate() - start.getDay());
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [cursor]);

  const today = new Date();

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => shift(-1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <p className="font-bold text-gray-800 min-w-[160px] text-center">
            {mode === 'day'
              ? cursor.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
              : cursor.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
          </p>
          <Button size="sm" variant="outline" onClick={() => shift(1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => setCursor(new Date())}>
            Today
          </Button>
        </div>
        <div className="flex rounded-xl border border-gray-200 overflow-hidden">
          {['month', 'week', 'day'].map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-3 py-1.5 text-sm font-medium capitalize transition-colors ${
                mode === m ? 'bg-emerald-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {mode === 'month' && (
        <div className="grid grid-cols-7 gap-1.5">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} className="text-center text-xs font-bold text-gray-400 py-1">
              {d}
            </div>
          ))}
          {monthGrid.map((d, i) => {
            const inMonth = d.getMonth() === cursor.getMonth();
            const dayAppts = apptsByDay.get(d.toDateString()) || [];
            return (
              <div
                key={i}
                className={`min-h-[80px] rounded-lg border p-1.5 ${
                  isSameDay(d, today) ? 'border-emerald-400 bg-emerald-50/50' : 'border-gray-100'
                } ${inMonth ? '' : 'opacity-40'}`}
              >
                <p className="text-xs font-semibold text-gray-500">{d.getDate()}</p>
                <div className="space-y-0.5 mt-1">
                  {dayAppts.slice(0, 2).map((a) => (
                    <button
                      key={a._id}
                      onClick={() => onView(a)}
                      className="w-full text-left text-[10px] leading-tight px-1 py-0.5 rounded bg-white border border-gray-100 hover:border-emerald-300 flex items-center gap-1 truncate"
                    >
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusDotColor(a.status)}`} />
                      <span className="truncate">{a.startTime} {a.doctor?.name}</span>
                    </button>
                  ))}
                  {dayAppts.length > 2 && <p className="text-[10px] text-gray-400 pl-1">+{dayAppts.length - 2} more</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {mode === 'week' && (
        <div className="grid grid-cols-7 gap-2">
          {weekDays.map((d, i) => {
            const dayAppts = apptsByDay.get(d.toDateString()) || [];
            return (
              <div key={i} className={`rounded-xl border p-2 min-h-[140px] ${isSameDay(d, today) ? 'border-emerald-400 bg-emerald-50/40' : 'border-gray-100'}`}>
                <p className="text-xs font-bold text-gray-500">{fmtWeekday(d)}</p>
                <p className="text-sm font-semibold text-gray-700 mb-2">{d.getDate()}</p>
                <div className="space-y-1">
                  {dayAppts.map((a) => (
                    <button
                      key={a._id}
                      onClick={() => onView(a)}
                      className="w-full text-left text-xs px-2 py-1 rounded-lg bg-white border border-gray-100 hover:border-emerald-300 flex items-center gap-1.5"
                    >
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusDotColor(a.status)}`} />
                      {a.startTime} &bull; {a.doctor?.name}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {mode === 'day' && (
        <div className="space-y-2">
          {(apptsByDay.get(cursor.toDateString()) || []).length === 0 ? (
            <EmptyState icon={CalendarDays} title="No appointments" description="Nothing scheduled for this day" />
          ) : (
            (apptsByDay.get(cursor.toDateString()) || [])
              .sort((a, b) => a.startTime?.localeCompare(b.startTime))
              .map((a) => (
                <button
                  key={a._id}
                  onClick={() => onView(a)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-emerald-300 hover:bg-emerald-50/30 text-left transition-all"
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusDotColor(a.status)}`} />
                  <span className="text-sm font-semibold text-gray-700 w-16">{a.startTime}</span>
                  <span className="text-sm text-gray-600 flex-1">
                    {a.therapy?.name} with Dr. {a.doctor?.name}
                  </span>
                  <StatusBadge status={a.status} />
                </button>
              ))
          )}
        </div>
      )}

      <div className="flex items-center gap-4 mt-5 pt-4 border-t border-gray-50 text-xs text-gray-500">
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Completed</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-500" /> Upcoming</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-rose-400" /> Cancelled</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-gray-400" /> Missed</span>
      </div>
    </Card>
  );
};

// ============================================================
// Appointment Details Modal
// ============================================================
const AppointmentDetailsModal = ({ appointment, onClose, onNeedsLink }) => {
  if (!appointment) return null;
  const a = appointment;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Appointment Details</h2>
            <p className="text-xs text-gray-400">Session #{a.sessionId || a._id?.slice(-6)}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-xl border border-gray-100 p-4">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Patient</p>
              <p className="font-semibold text-gray-800">{a.patient?.name || 'You'}</p>
              <p className="text-sm text-gray-500">{a.patient?.phone}</p>
            </div>
            <div className="rounded-xl border border-gray-100 p-4">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Doctor</p>
              <p className="font-semibold text-gray-800">Dr. {a.doctor?.name}</p>
              <p className="text-sm text-emerald-600">{a.doctor?.specialization}</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-2 text-gray-600"><Calendar className="w-4 h-4 text-emerald-500" />{fmt(a.date)}</div>
            <div className="flex items-center gap-2 text-gray-600"><Clock className="w-4 h-4 text-emerald-500" />{a.startTime} &bull; {a.duration || 30} min</div>
            <div className="flex items-center gap-2 text-gray-600">
              {a.type?.toLowerCase() === 'online' ? <Video className="w-4 h-4 text-emerald-500" /> : <MapPin className="w-4 h-4 text-emerald-500" />}
              {a.type?.toLowerCase() === 'online' ? 'Online Consultation' : a.clinicName || 'In-clinic'}
            </div>
          </div>

          {a.type?.toLowerCase() === 'online' && (
            <div className="rounded-xl border border-gray-100 p-4 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Meeting Information</p>
                <p className="text-sm text-gray-600">{a.meetingLink ? 'Link generated by your care team' : 'Link will appear closer to the session'}</p>
              </div>
              <JoinMeetingButton appointment={a} onNeedsLink={onNeedsLink} />
            </div>
          )}

          {a.symptoms && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Symptoms</p>
              <p className="text-sm text-gray-600">{a.symptoms}</p>
            </div>
          )}
          {a.diagnosis && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Diagnosis</p>
              <p className="text-sm text-gray-600">{a.diagnosis}</p>
            </div>
          )}
          {a.notes && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Appointment Notes</p>
              <p className="text-sm text-gray-600">{a.notes}</p>
            </div>
          )}
          {a.consultationSummary && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Consultation Summary</p>
              <p className="text-sm text-gray-600">{a.consultationSummary}</p>
            </div>
          )}

          {a.prescription && (
            <div className="rounded-xl bg-blue-50 border border-blue-100 p-4">
              <p className="text-xs font-bold text-blue-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Pill className="w-3.5 h-3.5" /> Prescription
              </p>
              <p className="text-sm text-gray-700">{a.prescription.summary || 'Prescription attached — view in Prescriptions tab.'}</p>
            </div>
          )}

          {a.attachments?.length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Medical Attachments</p>
              <div className="flex flex-wrap gap-2">
                {a.attachments.map((att, i) => (
                  <a
                    key={i}
                    href={att.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-100"
                  >
                    <Paperclip className="w-3.5 h-3.5" />
                    {att.name || 'Attachment'}
                  </a>
                ))}
              </div>
            </div>
          )}

          {a.followUpDate && (
            <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-sm text-amber-700 flex items-center gap-2">
              <CalendarClock className="w-4 h-4" />
              Follow-up scheduled for {fmt(a.followUpDate)}
            </div>
          )}

          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Progress Timeline</p>
            <AppointmentTimeline appointment={a} />
          </div>
        </div>
      </div>
    </div>
  );
};

// Simple reschedule modal
const RescheduleModal = ({ appointment, onClose, onConfirm }) => {
  const [date, setDate] = useState(appointment?.date ? new Date(appointment.date).toISOString().slice(0, 10) : '');
  const [time, setTime] = useState(appointment?.startTime || '');
  const [saving, setSaving] = useState(false);
  if (!appointment) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-gray-800 mb-1">Reschedule Appointment</h2>
        <p className="text-sm text-gray-500 mb-5">Dr. {appointment.doctor?.name} &bull; {appointment.therapy?.name}</p>
        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">New Date</label>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full mt-1 mb-4 px-3 py-2 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 outline-none" />
        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">New Time</label>
        <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="w-full mt-1 mb-6 px-3 py-2 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 outline-none" />
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={saving || !date || !time}
            onClick={async () => {
              setSaving(true);
              await onConfirm(appointment, { date, startTime: time });
              setSaving(false);
            }}
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Doctor review section
// ============================================================
const DoctorReviewSection = ({ appointment, onClose, onSubmitted }) => {
  const [review, setReview] = useState({
    overall: 0,
    communication: 0,
    professionalism: 0,
    treatment: 0,
    waitingTime: 0,
    review: '',
    anonymous: false,
  });
  const [submitting, setSubmitting] = useState(false);
  if (!appointment) return null;

  const set = (k, v) => setReview((r) => ({ ...r, [k]: v }));

  const submit = async () => {
    if (!review.overall) {
      toast.error('Please give an overall rating');
      return;
    }
    setSubmitting(true);
    try {
      if (appointmentsAPI.submitReview) {
        await appointmentsAPI.submitReview(appointment._id, review);
      }
      toast.success('Doctor review submitted');
      onSubmitted && onSubmitted(appointment);
      onClose();
    } catch (err) {
      toast.error('Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const ratingRows = [
    ['communication', 'Communication'],
    ['professionalism', 'Professionalism'],
    ['treatment', 'Treatment Satisfaction'],
    ['waitingTime', 'Waiting Time'],
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Review Dr. {appointment.doctor?.name}</h2>
            <p className="text-gray-500 text-sm">{appointment.therapy?.name} &bull; {fmt(appointment.date)}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="bg-emerald-50 rounded-xl p-4 mb-5 border border-emerald-100 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center overflow-hidden">
            {appointment.doctor?.photo ? (
              <img src={appointment.doctor.photo} className="w-full h-full object-cover" alt="" />
            ) : (
              <User className="w-7 h-7 text-emerald-600" />
            )}
          </div>
          <div>
            <p className="font-semibold">Overall Experience</p>
            <StarRating value={review.overall} onChange={(v) => set('overall', v)} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-5 mb-5">
          {ratingRows.map(([key, label]) => (
            <div key={key}>
              <p className="text-sm font-semibold mb-1.5">{label}</p>
              <StarRating value={review[key]} onChange={(v) => set(key, v)} size={18} />
            </div>
          ))}
        </div>

        <label className="text-sm font-semibold block mb-2">Write Review</label>
        <textarea
          rows={4}
          placeholder="Tell us about your experience with the doctor..."
          value={review.review}
          onChange={(e) => set('review', e.target.value)}
          className="w-full rounded-xl border border-gray-200 p-3 text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
        />

        <div className="flex items-center gap-2 mt-4">
          <input type="checkbox" checked={review.anonymous} onChange={(e) => set('anonymous', e.target.checked)} id="anon" />
          <label htmlFor="anon" className="text-sm text-gray-600">Submit anonymously</label>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Submit Review
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// App feedback section
// ============================================================
const AppFeedbackSection = () => {
  const [feedback, setFeedback] = useState({
    ui: 0,
    navigation: 0,
    booking: 0,
    performance: 0,
    ai: 0,
    overall: 0,
    suggestion: '',
    bugReport: '',
    featureRequest: '',
    recommend: true,
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setFeedback((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSubmitting(true);
    try {
      // Requires: POST /api/feedback/app — see backend contract comment at top of file.
      toast.success('Thank you for your feedback');
      setSubmitted(true);
    } catch {
      toast.error('Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const rows = [
    ['ui', 'UI Design'],
    ['navigation', 'Ease of Navigation'],
    ['booking', 'Appointment Booking Experience'],
    ['performance', 'Performance'],
    ['ai', 'AI Recommendations'],
  ];

  if (submitted) {
    return (
      <Card>
        <div className="flex flex-col items-center text-center py-8">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-800">Thank you for your feedback!</h3>
          <p className="text-sm text-gray-500 mt-1">Your input helps us improve Avartana Care.</p>
          <Button variant="outline" className="mt-4" onClick={() => setSubmitted(false)}>
            Submit More Feedback
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title="Avartana Care Feedback"
      subtitle="Help us improve your experience"
      action={
        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
          <MessageSquare className="w-5 h-5 text-blue-500" />
        </div>
      }
    >
      <div className="grid sm:grid-cols-2 gap-5 mb-5">
        {rows.map(([key, label]) => (
          <div key={key}>
            <p className="text-sm font-semibold mb-1.5">{label}</p>
            <StarRating value={feedback[key]} onChange={(v) => set(key, v)} size={18} />
          </div>
        ))}
      </div>

      <div className="bg-gray-50 rounded-xl p-4 mb-5">
        <p className="text-sm font-semibold mb-1.5">Overall Satisfaction</p>
        <StarRating value={feedback.overall} onChange={(v) => set('overall', v)} />
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5 mb-1.5">
            <Lightbulb className="w-3.5 h-3.5" /> Feature Request
          </label>
          <textarea
            rows={3}
            value={feedback.featureRequest}
            onChange={(e) => set('featureRequest', e.target.value)}
            placeholder="What would you like to see?"
            className="w-full rounded-xl border border-gray-200 p-3 text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
          />
        </div>
        <div>
          <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5 mb-1.5">
            <Bug className="w-3.5 h-3.5" /> Bug Report
          </label>
          <textarea
            rows={3}
            value={feedback.bugReport}
            onChange={(e) => set('bugReport', e.target.value)}
            placeholder="Ran into something broken?"
            className="w-full rounded-xl border border-gray-200 p-3 text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
          />
        </div>
      </div>

      <label className="text-xs font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Suggestion Box</label>
      <textarea
        rows={3}
        value={feedback.suggestion}
        onChange={(e) => set('suggestion', e.target.value)}
        placeholder="Anything else you'd like to share..."
        className="w-full rounded-xl border border-gray-200 p-3 text-sm focus:ring-2 focus:ring-emerald-500 outline-none mb-5"
      />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <p className="text-sm font-semibold">Would you recommend Avartana Care?</p>
          <button
            onClick={() => set('recommend', true)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              feedback.recommend ? 'bg-emerald-500 text-white border-emerald-500' : 'border-gray-200 text-gray-500'
            }`}
          >
            <ThumbsUp className="w-3.5 h-3.5" /> Yes
          </button>
          <button
            onClick={() => set('recommend', false)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              !feedback.recommend ? 'bg-rose-500 text-white border-rose-500' : 'border-gray-200 text-gray-500'
            }`}
          >
            <ThumbsDown className="w-3.5 h-3.5" /> No
          </button>
        </div>
        <Button onClick={submit} disabled={submitting}>
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          Submit Feedback
        </Button>
      </div>
    </Card>
  );
};

// ============================================================
// MAIN: Patient Appointments Dashboard
// ============================================================
const TABS = [
  { key: 'overview', label: 'Overview', icon: Activity },
  { key: 'upcoming', label: 'Upcoming', icon: CalendarClock },
  { key: 'history', label: 'History', icon: History },
  { key: 'calendar', label: 'Calendar', icon: CalendarDays },
  { key: 'feedback', label: 'Feedback', icon: MessageSquare },
];

export const PatientAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  const [detailsAppt, setDetailsAppt] = useState(null);
  const [rescheduleAppt, setRescheduleAppt] = useState(null);
  const [reviewAppt, setReviewAppt] = useState(null);
  const [inspectDoctor, setInspectDoctor] = useState(null);
// ── DEV-ONLY sample data — shows the dashboard populated during local
// preview when the backend has no appointments yet. Never used in
// production builds (guarded by NODE_ENV) and safe to delete once your
// backend is seeded with real data.
const MOCK_APPOINTMENTS = [
  {
    _id: 'mock1',
    sessionId: 'AVC-1042',
    doctor: { name: 'Priya Menon', specialization: 'Physiotherapy', photo: '' },
    therapy: { name: 'Post-Surgery Rehab' },
    date: new Date().toISOString(),
    startTime: (() => {
      const d = new Date();
      d.setMinutes(d.getMinutes() + 8);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    })(),
    duration: 30,
    type: 'Online',
    status: 'confirmed',
    meetingLink: 'https://meet.google.com/abc-defg-hij',
    notes: 'Bring your last mobility assessment report.',
  },
  {
    _id: 'mock2',
    sessionId: 'AVC-1043',
    doctor: { name: 'Arvind Rao', specialization: 'Orthopedics', photo: '' },
    therapy: { name: 'Knee Strengthening' },
    date: (() => {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      return d.toISOString();
    })(),
    startTime: '11:30',
    duration: 45,
    type: 'Offline',
    clinicName: 'Avartana Care — Anna Nagar Clinic',
    status: 'pending',
  },
  {
    _id: 'mock3',
    sessionId: 'AVC-0988',
    doctor: { name: 'Priya Menon', specialization: 'Physiotherapy', photo: '' },
    therapy: { name: 'Post-Surgery Rehab' },
    date: (() => {
      const d = new Date();
      d.setDate(d.getDate() - 5);
      return d.toISOString();
    })(),
    startTime: '10:00',
    duration: 30,
    type: 'Online',
    status: 'completed',
    doctorNotes: 'Great progress — range of motion improved by 15%.',
    prescriptionAvailable: true,
    reportAvailable: true,
    symptoms: 'Mild stiffness in the left knee after exercise.',
    diagnosis: 'Recovering well post ACL surgery, no complications.',
    consultationSummary: 'Continue current exercise plan, add light resistance band work.',
    followUpDate: (() => {
      const d = new Date();
      d.setDate(d.getDate() + 9);
      return d.toISOString();
    })(),
  },
  {
    _id: 'mock4',
    sessionId: 'AVC-0951',
    doctor: { name: 'Sneha Iyer', specialization: 'Nutrition & Diet', photo: '' },
    therapy: { name: 'Diet Consultation' },
    date: (() => {
      const d = new Date();
      d.setDate(d.getDate() - 12);
      return d.toISOString();
    })(),
    startTime: '16:00',
    duration: 20,
    type: 'Online',
    status: 'completed',
    doctorNotes: 'Cut down on processed sugar, added protein target.',
    prescriptionAvailable: false,
    reportAvailable: false,
    reviewed: true,
  },
  {
    _id: 'mock5',
    sessionId: 'AVC-0902',
    doctor: { name: 'Arvind Rao', specialization: 'Orthopedics', photo: '' },
    therapy: { name: 'Knee Strengthening' },
    date: (() => {
      const d = new Date();
      d.setDate(d.getDate() - 20);
      return d.toISOString();
    })(),
    startTime: '09:00',
    duration: 30,
    type: 'Offline',
    clinicName: 'Avartana Care — Anna Nagar Clinic',
    status: 'cancelled',
  },
  {
    _id: 'mock6',
    sessionId: 'AVC-0876',
    doctor: { name: 'Sneha Iyer', specialization: 'Nutrition & Diet', photo: '' },
    therapy: { name: 'Diet Consultation' },
    date: (() => {
      const d = new Date();
      d.setDate(d.getDate() - 27);
      return d.toISOString();
    })(),
    startTime: '17:30',
    duration: 20,
    type: 'Online',
    status: 'missed',
  },
];
const loadAppointments = () => {
  setLoading(true);
  return appointmentsAPI
    .getAll()
    .then((r) => {
      const real = r.data.appointments || [];
      setAppointments(real);
    })
    .catch(() => {
      setAppointments([]);
      toast.error('Failed to load appointments');
    })
    .finally(() => setLoading(false));
};

  useEffect(() => {
    loadAppointments();
    notificationsAPI
      .getAll()
      .then((r) => setNotifications(r.data.notifications || []))
      .catch(() => {});
  }, []);

  const stats = useMemo(() => {
    const total = appointments.length;
    const upcoming = appointments.filter((a) => ['pending', 'confirmed', 'upcoming'].includes(a.status)).length;
    const completed = appointments.filter((a) => a.status === 'completed').length;
    const cancelled = appointments.filter((a) => a.status === 'cancelled').length;
    const missed = appointments.filter((a) => a.status === 'missed').length;
    return { total, upcoming, completed, cancelled, missed };
  }, [appointments]);

  const upcomingList = useMemo(
    () =>
      appointments
        .filter((a) => ['pending', 'confirmed', 'upcoming'].includes(a.status))
        .sort((a, b) => (combineDateTime(a.date, a.startTime) || 0) - (combineDateTime(b.date, b.startTime) || 0)),
    [appointments]
  );

  const handleReschedule = async (appt, payload) => {
    try {
      if (appointmentsAPI.reschedule) {
        await appointmentsAPI.reschedule(appt._id, payload);
        toast.success('Appointment rescheduled');
        loadAppointments();
      } else {
        toast.error('Reschedule endpoint not configured yet');
      }
    } catch {
      toast.error('Could not reschedule appointment');
    } finally {
      setRescheduleAppt(null);
    }
  };

  const handleCancel = async (appt) => {
    if (!window.confirm('Cancel this appointment?')) return;
    try {
      if (appointmentsAPI.cancel) {
        await appointmentsAPI.cancel(appt._id, {});
        toast.success('Appointment cancelled');
        loadAppointments();
      } else {
        toast.error('Cancel endpoint not configured yet');
      }
    } catch {
      toast.error('Could not cancel appointment');
    }
  };

  const handleNeedsLink = () => {
    toast('Meeting link will be available shortly before your session starts', { icon: '🔔' });
  };

  if (loading) {
    return (
      <DashboardLayout navItems={NAV_ITEMS} role="patient">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-28 rounded-2xl bg-gray-100 animate-pulse" />
          ))}
        </div>
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={NAV_ITEMS} role="patient">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">My Appointments</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your consultations, history, and care journey</p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <StatCard icon={Calendar} label="Total Appointments" value={stats.total} tone="emerald" />
        <StatCard icon={CalendarClock} label="Upcoming" value={stats.upcoming} tone="blue" />
        <StatCard icon={CheckCircle} label="Completed" value={stats.completed} tone="emerald" />
        <StatCard icon={XCircle} label="Cancelled" value={stats.cancelled} tone="red" />
        <StatCard icon={AlertCircle} label="Missed" value={stats.missed} tone="amber" />
      </div>

      <SmartReminders appointments={appointments} notifications={notifications} />

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-100 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors flex-shrink-0 ${
              tab === t.key ? 'border-emerald-500 text-emerald-600' : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-bold text-gray-800 mb-3">Upcoming Appointments</h2>
            {upcomingList.length === 0 ? (
              <Card>
                <EmptyState icon={CalendarClock} title="No upcoming appointments" description="Book a session to see it here" />
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {upcomingList.slice(0, 4).map((a) => (
                  <UpcomingAppointmentCard
                    key={a._id}
                    appt={a}
                    onView={setDetailsAppt}
                    onReschedule={setRescheduleAppt}
                    onCancel={handleCancel}
                    onNeedsLink={handleNeedsLink}
                    onInspectDoctor={setInspectDoctor}
                  />
                ))}
              </div>
            )}
          </div>
          <PastAppointmentHistory appointments={appointments} onView={setDetailsAppt} onReview={setReviewAppt} />
        </div>
      )}

      {tab === 'upcoming' && (
        <div>
          {upcomingList.length === 0 ? (
            <Card>
              <EmptyState icon={CalendarClock} title="No upcoming appointments" description="Book a session to see it here" />
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {upcomingList.map((a) => (
                <UpcomingAppointmentCard
                  key={a._id}
                  appt={a}
                  onView={setDetailsAppt}
                  onReschedule={setRescheduleAppt}
                  onCancel={handleCancel}
                  onNeedsLink={handleNeedsLink}
                  onInspectDoctor={setInspectDoctor}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'history' && <PastAppointmentHistory appointments={appointments} onView={setDetailsAppt} onReview={setReviewAppt} />}

      {tab === 'calendar' && <CalendarView appointments={appointments} onView={setDetailsAppt} />}

      {tab === 'feedback' && <AppFeedbackSection />}

      {/* Modals */}
      <AppointmentDetailsModal appointment={detailsAppt} onClose={() => setDetailsAppt(null)} onNeedsLink={handleNeedsLink} />
      <RescheduleModal appointment={rescheduleAppt} onClose={() => setRescheduleAppt(null)} onConfirm={handleReschedule} />
      <DoctorReviewSection
        appointment={reviewAppt}
        onClose={() => setReviewAppt(null)}
        onSubmitted={(appt) => setAppointments((prev) => prev.map((a) => (a._id === appt._id ? { ...a, reviewed: true } : a)))}
      />
      <DoctorLicenseModal isOpen={!!inspectDoctor} onClose={() => setInspectDoctor(null)} doctor={inspectDoctor} />
    </DashboardLayout>
  );
};
// ==========================================================
// 3. DOCTOR — PRESCRIBE (CREATE PRESCRIPTION FOR PATIENT)
// ==========================================================

const emptyMedicine = () => ({
  name: '',
  dosage: '',
  frequency: '',
  duration: '',
  timing: 'after-meal',
  notes: '',
});

const TIMING_OPTIONS = [
  { value: 'before-meal', label: 'Before meal' },
  { value: 'after-meal', label: 'After meal' },
  { value: 'empty-stomach', label: 'Empty stomach' },
  { value: 'bedtime', label: 'Bedtime' },
  { value: 'anytime', label: 'Anytime' },
];

const TagInput = ({ value, onChange, onAdd, placeholder }) => (
  <div className="flex gap-2">
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onAdd();
        }
      }}
      placeholder={placeholder}
      className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
    <Button variant="outline" size="sm" onClick={onAdd}>
      <Plus className="w-3.5 h-3.5" />
    </Button>
  </div>
);

export const DoctorPrescribeForm = () => {
  const [patients, setPatients] = useState([]);
  const [patientQuery, setPatientQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showPatientList, setShowPatientList] = useState(false);
  const [loadingPatients, setLoadingPatients] = useState(true);

  const [medicines, setMedicines] = useState([emptyMedicine()]);
  const [dietaryAdvice, setDietaryAdvice] = useState([]);
  const [restrictions, setRestrictions] = useState([]);
  const [dietaryInput, setDietaryInput] = useState('');
  const [restrictionInput, setRestrictionInput] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    patientsAPI
      .getAll()
      .then((r) => setPatients(r.data.patients || []))
      .catch(() => toast.error('Failed to load patients'))
      .finally(() => setLoadingPatients(false));
  }, []);

  const filteredPatients = patients.filter((p) =>
    p.name?.toLowerCase().includes(patientQuery.toLowerCase())
  );

  const updateMedicine = (index, field, value) => {
    setMedicines((prev) =>
      prev.map((m, i) => (i === index ? { ...m, [field]: value } : m))
    );
  };

  const addMedicine = () => setMedicines((prev) => [...prev, emptyMedicine()]);

  const removeMedicine = (index) =>
    setMedicines((prev) => prev.filter((_, i) => i !== index));

  const addTag = (input, setInput, list, setList) => {
    const value = input.trim();
    if (!value) return;
    if (!list.includes(value)) setList([...list, value]);
    setInput('');
  };

  const removeTag = (value, list, setList) =>
    setList(list.filter((v) => v !== value));

  const validate = () => {
    if (!selectedPatient) {
      toast.error('Select a patient');
      return false;
    }
    const validMedicines = medicines.filter((m) => m.name.trim());
    if (validMedicines.length === 0) {
      toast.error('Add at least one medicine');
      return false;
    }
    for (const m of validMedicines) {
      if (!m.dosage.trim() || !m.frequency.trim() || !m.duration.trim()) {
        toast.error(`Complete dosage, frequency, and duration for ${m.name}`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await prescriptionsAPI.create({
        patientId: selectedPatient._id,
        medicines: medicines.filter((m) => m.name.trim()),
        dietaryAdvice,
        restrictions,
        followUpDate: followUpDate || undefined,
      });
      toast.success('Prescription sent to patient');
      setSelectedPatient(null);
      setPatientQuery('');
      setMedicines([emptyMedicine()]);
      setDietaryAdvice([]);
      setRestrictions([]);
      setFollowUpDate('');
    } catch {
      toast.error('Failed to create prescription');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingPatients)
    return (
      <DashboardLayout navItems={NAV_ITEMS} role="doctor">
        <LoadingState />
      </DashboardLayout>
    );

  return (
    <DashboardLayout navItems={NAV_ITEMS} role="doctor">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">New Prescription</h1>

      <div className="space-y-5">
        <Card title="Patient">
          {selectedPatient ? (
            <div className="flex items-center justify-between bg-blue-50 rounded-xl p-3">
              <div>
                <p className="font-semibold text-gray-800 text-sm">{selectedPatient.name}</p>
                <p className="text-xs text-gray-500">{selectedPatient.email}</p>
              </div>
              <button
                onClick={() => {
                  setSelectedPatient(null);
                  setPatientQuery('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="relative">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={patientQuery}
                  onChange={(e) => {
                    setPatientQuery(e.target.value);
                    setShowPatientList(true);
                  }}
                  onFocus={() => setShowPatientList(true)}
                  placeholder="Search patient by name"
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {showPatientList && patientQuery && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
                  {filteredPatients.length === 0 ? (
                    <p className="text-sm text-gray-400 px-3 py-3">No patients found</p>
                  ) : (
                    filteredPatients.map((p) => (
                      <button
                        key={p._id}
                        onClick={() => {
                          setSelectedPatient(p);
                          setShowPatientList(false);
                        }}
                        className="w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50 flex flex-col"
                      >
                        <span className="font-medium text-gray-800">{p.name}</span>
                        <span className="text-xs text-gray-500">{p.email}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </Card>

        <Card
          title="Medicines"
          action={
            <Button variant="outline" size="sm" onClick={addMedicine}>
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add medicine
            </Button>
          }
        >
          <div className="space-y-4">
            {medicines.map((m, i) => (
              <div key={i} className="p-4 bg-gray-50 rounded-xl relative">
                {medicines.length > 1 && (
                  <button
                    onClick={() => removeMedicine(i)}
                    className="absolute top-3 right-3 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="col-span-2">
                    <label className="text-xs font-semibold text-gray-500 mb-1 block">
                      Medicine name
                    </label>
                    <input
                      value={m.name}
                      onChange={(e) => updateMedicine(i, 'name', e.target.value)}
                      placeholder="e.g. Amoxicillin 500mg"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 mb-1 block">
                      Dosage
                    </label>
                    <input
                      value={m.dosage}
                      onChange={(e) => updateMedicine(i, 'dosage', e.target.value)}
                      placeholder="1 tablet"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 mb-1 block">
                      Frequency
                    </label>
                    <input
                      value={m.frequency}
                      onChange={(e) => updateMedicine(i, 'frequency', e.target.value)}
                      placeholder="Twice daily"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 mb-1 block">
                      Duration
                    </label>
                    <input
                      value={m.duration}
                      onChange={(e) => updateMedicine(i, 'duration', e.target.value)}
                      placeholder="5 days"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 mb-1 block">
                      Timing
                    </label>
                    <select
                      value={m.timing}
                      onChange={(e) => updateMedicine(i, 'timing', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {TIMING_OPTIONS.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <input
                  value={m.notes}
                  onChange={(e) => updateMedicine(i, 'notes', e.target.value)}
                  placeholder="Note (optional)"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ))}
          </div>
        </Card>

        <Card title="Dietary advice">
          <TagInput
            value={dietaryInput}
            onChange={setDietaryInput}
            onAdd={() => addTag(dietaryInput, setDietaryInput, dietaryAdvice, setDietaryAdvice)}
            placeholder="e.g. Drink plenty of fluids"
          />
          <div className="flex flex-wrap gap-2 mt-3">
            {dietaryAdvice.map((d) => (
              <Badge key={d} color="emerald">
                <span className="flex items-center gap-1.5">
                  {d}
                  <button onClick={() => removeTag(d, dietaryAdvice, setDietaryAdvice)}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              </Badge>
            ))}
          </div>
        </Card>

        <Card title="Restrictions">
          <TagInput
            value={restrictionInput}
            onChange={setRestrictionInput}
            onAdd={() =>
              addTag(restrictionInput, setRestrictionInput, restrictions, setRestrictions)
            }
            placeholder="e.g. Avoid dairy products"
          />
          <div className="flex flex-wrap gap-2 mt-3">
            {restrictions.map((r) => (
              <Badge key={r} color="red">
                <span className="flex items-center gap-1.5">
                  {r}
                  <button onClick={() => removeTag(r, restrictions, setRestrictions)}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              </Badge>
            ))}
          </div>
        </Card>

        <Card title="Follow-up">
          <div className="relative max-w-xs">
            <Calendar className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="date"
              value={followUpDate}
              onChange={(e) => setFollowUpDate(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </Card>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={() => window.history.back()}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            <Pill className="w-4 h-4 mr-1.5" />
            {submitting ? 'Sending…' : 'Send prescription'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};
// ============================================================
// 2. PRESCRIPTIONS
// ============================================================
export const PatientPrescriptions = () => {
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    prescriptionsAPI
      .getAll()
      .then((r) => setPrescriptions(r.data.prescriptions || []))
      .catch(() => toast.error('Failed to load prescriptions'))
      .finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <DashboardLayout navItems={NAV_ITEMS} role="patient">
        <LoadingState />
      </DashboardLayout>
    );

  return (
    <DashboardLayout navItems={NAV_ITEMS} role="patient">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">My Prescriptions</h1>

      {prescriptions.length === 0 ? (
        <Card>
          <EmptyState
            icon={Pill}
            title="No prescriptions yet"
            description="Prescriptions from your doctor will appear here"
          />
        </Card>
      ) : (
        <div className="space-y-5">
          {prescriptions.map((p) => (
            <Card
              key={p._id}
              title={`Prescription — ${fmt(p.createdAt)}`}
              subtitle={`By ${p.doctor?.name}`}
              action={
                <Button variant="outline" size="sm" onClick={() => window.print()}>
                  Download PDF
                </Button>
              }
            >
              {p.medicines?.length > 0 && (
                <div className="mb-5">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                    Medicines
                  </p>
                  <div className="space-y-2">
                    {p.medicines.map((m, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl">
                        <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Pill className="w-3.5 h-3.5 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-800 text-sm">{m.name}</p>
                          <p className="text-xs text-gray-500">
                            {m.dosage} &bull; {m.frequency} &bull; {m.duration} &bull;{' '}
                            {m.timing?.replace(/-/g, ' ')}
                          </p>
                          {m.notes && (
                            <p className="text-xs text-gray-400 italic mt-0.5">Note: {m.notes}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {p.dietaryAdvice?.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                    Dietary Advice
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {p.dietaryAdvice.map((d, i) => <Badge key={i} color="emerald">{d}</Badge>)}
                  </div>
                </div>
              )}

              {p.restrictions?.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                    Restrictions
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {p.restrictions.map((r, i) => <Badge key={i} color="red">{r}</Badge>)}
                  </div>
                </div>
              )}

              {p.followUpDate && (
                <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 mt-4">
                  📅 Follow-up: {fmt(p.followUpDate)}
                </p>
              )}
            </Card>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
};

// ============================================================
// 3. THERAPY PROGRESS
// ============================================================
export const PatientProgress = () => {
  const { user } = useAuth();
  const [profileUser, setProfileUser] = useState(user);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        if (user?._id) {
          const res = await usersAPI.getById(user._id);
          if (res.data.user) setProfileUser(res.data.user);
        }
        const apptRes = await appointmentsAPI.getAll({ status: 'completed' });
        setAppointments(apptRes.data.appointments || []);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?._id]);

  const p = profileUser?.patientProfile || {};
  const progress = p.therapyProgress || 0;
  const doctor = p.assignedDoctor;
  const therapyName = p.currentTherapy?.name || (typeof p.currentTherapy === 'string' ? p.currentTherapy : 'Assigned Therapy Plan');

  const bars = [
    { label: 'Overall Therapy Progress',  value: progress, bg: 'bg-emerald-500' },
    { label: 'Diet Compliance',  value: 64,        bg: 'bg-teal-500'   },
    { label: 'Meditation & Yoga',  value: 85,        bg: 'bg-blue-500'   },
    { label: 'Sleep Quality',    value: 72,        bg: 'bg-violet-500' },
    { label: 'Lifestyle Score',  value: 45,        bg: 'bg-amber-500'  },
  ];

  if (loading) return <DashboardLayout navItems={NAV_ITEMS} role="patient"><LoadingState /></DashboardLayout>;

  return (
    <DashboardLayout navItems={NAV_ITEMS} role="patient">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Therapy Progress</h1>
          {doctor?.name && (
            <p className="text-sm text-gray-500">
              Attending Doctor: <span className="font-semibold text-emerald-600">Dr. {doctor.name}</span>
            </p>
          )}
        </div>
      </div>

      {/* Therapy Overview Header */}
      <div className="mb-6 bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-100 rounded-2xl p-6 border border-emerald-100 shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2 flex-1">
            <Badge color="emerald">Current Plan</Badge>
            <h2 className="text-xl font-bold text-gray-800">{therapyName}</h2>
            {p.targetSessions > 0 && (
              <p className="text-sm text-gray-600">
                Target Sessions: <span className="font-bold text-emerald-700">{p.targetSessions}</span>
              </p>
            )}
            {p.therapyNotes && (
              <div className="bg-white/90 rounded-xl p-3 text-xs text-gray-700 border border-emerald-200 mt-2 shadow-xs">
                <span className="font-semibold text-emerald-800">Doctor's Plan & Instructions:</span> {p.therapyNotes}
              </div>
            )}
          </div>
          <div className="text-center bg-white p-4 rounded-2xl border border-emerald-100 shadow-sm min-w-[160px] self-stretch md:self-auto flex flex-col justify-center">
            <p className="text-3xl font-extrabold text-emerald-600">{progress}%</p>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-1">Completed</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Metrics */}
        <Card title="Progress Metrics">
          <div className="space-y-5">
            {bars.map((b) => (
              <div key={b.label}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-gray-600 font-medium">{b.label}</span>
                  <span className="text-gray-500 font-bold">{b.value}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full ${b.bg} transition-all duration-1000`}
                    style={{ width: `${b.value}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5">
            {progress >= 100 ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <p className="text-emerald-700 text-sm font-medium">
                  Congratulations! Therapy completed successfully!
                </p>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                <p className="text-blue-700 text-sm">
                  {100 - progress}% remaining — keep up the great work! 💪
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Completed sessions & Doctor Feedback */}
        <div className="space-y-6">
          <Card title="Completed Sessions">
            <div className="space-y-3">
              {appointments.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-6">
                  No completed sessions yet
                </p>
              ) : (
                appointments.slice(0, 6).map((a) => (
                  <div key={a._id} className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Activity className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {a.therapy?.name || 'Session'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {fmt(a.date)} at {a.startTime}
                      </p>
                    </div>
                    <StatusBadge status="completed" />
                  </div>
                ))
              )}
            </div>
          </Card>

          {/* Doctor Video Check-in Reviews */}
          {p.videoCheckins && p.videoCheckins.length > 0 && (
            <Card title="Doctor Feedback on Daily Check-ins">
              <div className="space-y-3">
                {p.videoCheckins.map((v, idx) => (
                  <div key={idx} className="p-3 rounded-xl border border-gray-100 bg-gray-50">
                    <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                      <span>{fmt(v.date)}</span>
                      {v.reviewed ? <Badge color="emerald">Reviewed by Doctor</Badge> : <Badge color="amber">Pending Review</Badge>}
                    </div>
                    {v.note && <p className="text-xs text-gray-700 mb-1 font-medium">Your Note: {v.note}</p>}
                    {v.doctorComment ? (
                      <div className="bg-emerald-50 text-emerald-800 text-xs p-2 rounded-lg mt-2 border border-emerald-200">
                        <span className="font-bold">Doctor Comment:</span> {v.doctorComment}
                      </div>
                    ) : (
                      <p className="text-[11px] text-gray-400 italic">No comments yet from doctor</p>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

// ============================================================
// 4. NOTIFICATIONS
// ============================================================
export const PatientNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    notificationsAPI
      .getAll()
      .then((r) => setNotifications(r.data.notifications || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const markRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, isRead: true } : n))
      );
    } catch {
      toast.error('Failed to mark as read');
    }
  };

  const markAllRead = async () => {
    try {
      await notificationsAPI.markRead('all');
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      toast.success('All marked as read');
    } catch {
      toast.error('Failed');
    }
  };

  if (loading)
    return (
      <DashboardLayout navItems={NAV_ITEMS} role="patient">
        <LoadingState />
      </DashboardLayout>
    );

  const typeColors = {
    appointment: 'blue', prescription: 'emerald', therapy: 'teal',
    alert: 'red', system: 'gray', reminder: 'amber',
  };

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <DashboardLayout navItems={NAV_ITEMS} role="patient">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Notifications</h1>
          {unreadCount > 0 && (
            <p className="text-sm text-gray-500 mt-0.5">{unreadCount} unread</p>
          )}
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" size="sm" onClick={markAllRead}>
            Mark all as read
          </Button>
        )}
      </div>

      <Card>
        {notifications.length === 0 ? (
          <EmptyState
            icon={Bell}
            title="No notifications"
            description="Your alerts and reminders will appear here"
          />
        ) : (
          <div className="space-y-2">
            {notifications.map((n) => (
              <div
                key={n._id}
                onClick={() => !n.isRead && markRead(n._id)}
                className={`flex items-start gap-4 p-4 rounded-xl border cursor-pointer transition-all hover:shadow-sm
                  ${n.isRead
                    ? 'bg-gray-50 border-gray-100 opacity-70'
                    : 'bg-white border-emerald-200 shadow-sm'
                  }`}
              >
                <div
                  className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${
                    n.isRead ? 'bg-gray-300' : 'bg-emerald-500'
                  }`}
                />
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <p className="font-semibold text-gray-800 text-sm">{n.title}</p>
                    <Badge color={typeColors[n.type] || 'gray'}>{n.type}</Badge>
                    {n.priority === 'high' && <Badge color="red">High</Badge>}
                  </div>
                  <p className="text-gray-500 text-sm">{n.message}</p>
                  <p className="text-gray-400 text-xs mt-1">
                    {new Date(n.createdAt).toLocaleString('en-IN')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </DashboardLayout>
  );
};

